import {
  listPublicKeys as listPublicKeysFromDatabase,
  registerPublicKey as registerPublicKeyInDatabase,
  revokePublicKey as revokePublicKeyInDatabase,
} from '@mesa/core/auth/public-keys';
import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { assertScope, baseAuthMiddleware } from '../middleware/base-auth';

const orgInputSchema = z.object({ orgSlug: z.string().trim().min(1).max(100) }).strict();
const registerKeyInputSchema = orgInputSchema.extend({ publicKey: z.string().trim().min(1).max(512) }).strict();
const revokeKeyInputSchema = orgInputSchema.extend({ keyId: z.string().trim().min(1).max(128) }).strict();

export const listPublicKeysServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data) => orgInputSchema.parse(data))
  .handler(async ({ context: { org, member } }) => {
    assertScope(member.role, 'read');
    return (await listPublicKeysFromDatabase(org.id)).map(({ id, publicKey, lastUsedAt, createdAt }) => ({
      id,
      publicKey,
      lastUsedAt,
      createdAt,
    }));
  });

export const registerPublicKeyServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data) => registerKeyInputSchema.parse(data))
  .handler(async ({ data: { publicKey }, context: { org, member, session } }) => {
    assertScope(member.role, 'admin');
    await registerPublicKeyInDatabase({ orgId: org.id, userId: session.user.id, publicKey });
  });

export const revokePublicKeyServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data) => revokeKeyInputSchema.parse(data))
  .handler(async ({ data: { keyId }, context: { org, member } }) => {
    assertScope(member.role, 'admin');
    await revokePublicKeyInDatabase({ orgId: org.id, keyId });
  });

export type PublicKey = Awaited<ReturnType<typeof listPublicKeysServerFn>>[number];
